import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, { Background, Controls, MiniMap, Node } from 'reactflow';
import 'reactflow/dist/style.css';

import { HostNode, VmLinkEdge, VmNode } from '@components';
import { ServerParkListView, ServerParkSummary } from '@containers';
import { useAside, useCompany, useServerTopologyModel } from '@hooks';
import {
  AssetServer,
  NinjaDevice,
  RmmState,
  RmmStatus,
  TopologyNodeData,
} from '@interfaces';
import { AssetsService } from '@services';

function epochToIso(epochSeconds?: number) {
  if (!epochSeconds) return undefined;
  return new Date(epochSeconds * 1000).toISOString();
}

function computeStatusFromNinja(d: NinjaDevice): {
  status: RmmStatus;
  reasons: string[];
} {
  const reasons: string[] = [];
  if (d.offline) return { status: 'offline', reasons: ['Device offline'] };

  if (d.os?.needsReboot) reasons.push('Pending reboot');

  const lowDisk = (d.volumes ?? []).some(
    (v) => v.capacity > 0 && v.freeSpace / v.capacity < 0.1
  );
  if (lowDisk) reasons.push('Low disk space');

  if (reasons.length > 0) return { status: 'warning', reasons };
  return { status: 'online', reasons: [] };
}

async function asyncPool<T>(
  limit: number,
  items: T[],
  iterator: (item: T) => Promise<void>
) {
  const executing = new Set<Promise<void>>();

  for (const item of items) {
    const p = (async () => iterator(item))().finally(() => executing.delete(p));
    executing.add(p);

    if (executing.size >= limit) {
      await Promise.race(executing);
    }
  }

  await Promise.all(executing);
}

function miniMapColor(n: Node) {
  const d: any = n.data;
  const rmm: RmmState | undefined = d?.rmm;

  if (rmm?.present) {
    if (rmm.loading) return '#94a3b8';
    if (rmm.status === 'online') return '#7fe0a2';
    if (rmm.status === 'offline') return '#f09191';
    if (rmm.status === 'warning') return '#fdcd7a';
  }
  return n.type === 'host' ? '#0ea5e9' : '#f8e3bf';
}

interface Props {
  servers: AssetServer[];
}

export function AssetServersTopology({ servers }: Props) {
  const [query, setQuery] = useState('');
  const [showOnlyRunning, setShowOnlyRunning] = useState<boolean>(false);
  const [view, setView] = useState<'topology' | 'list'>('topology');
  const [rmmBySerial, setRmmBySerial] = useState<Record<string, RmmState>>({});

  const { companyId } = useCompany();
  const { openAside } = useAside();

  const nodeTypes = { host: HostNode, vm: VmNode };
  const edgeTypes = { vmLink: VmLinkEdge };

  const abortRef = useRef<AbortController | null>(null);
  const assetService = useMemo(() => new AssetsService(), []);
  const [hosts, setHosts] = useState();
  const openNodeAside = (node: Node<TopologyNodeData> | null) => {
    openAside({
      component: 'serverDetailContainer',
      data: { node },
      title: 'Server',
    });
  };

  const fetchNinjaBySerial = async (
    serial: string
  ): Promise<NinjaDevice | null> => {
    if (!companyId) return null;

    try {
      return await assetService.getBySerialOnRmm(serial, Number(companyId));
    } catch (e: any) {
      if (e?.message?.includes('404')) return null;
      throw e;
    }
  };

  useEffect(() => {
    if (!servers.length || !companyId) return;

    const hostSerials = servers
      .map((s) => s.payload.serialNumber)
      .filter(Boolean) as string[];

    const vmSerials = servers
      .flatMap((s) =>
        (s.payload.vms ?? []).map((v) => v.serialNumber).filter(Boolean)
      )
      .filter(Boolean) as string[];

    const serials = Array.from(new Set([...hostSerials, ...vmSerials]));
    if (!serials.length) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRmmBySerial((prev) => {
      const next = { ...prev };
      for (const sn of serials) {
        next[sn] = next[sn]
          ? { ...next[sn], loading: true }
          : { loading: true, present: false, status: 'unknown' };
      }
      return next;
    });

    (async () => {
      const CONCURRENCY = 6;

      await asyncPool(CONCURRENCY, serials, async (serial) => {
        if (controller.signal.aborted) return;

        try {
          const device = await fetchNinjaBySerial(serial);

          if (!device) {
            setRmmBySerial((prev) => ({
              ...prev,
              [serial]: { loading: false, present: false, status: 'unknown' },
            }));
            return;
          }

          const { status, reasons } = computeStatusFromNinja(device);

          setRmmBySerial((prev) => ({
            ...prev,
            [serial]: {
              loading: false,
              present: true,
              status,
              reasons,
              deviceId: device.id,
              nodeClass: device.nodeClass,
              lastContact: epochToIso(device.lastContact),
            },
          }));
        } catch (e: any) {
          if (e?.name === 'AbortError') return;

          setRmmBySerial((prev) => ({
            ...prev,
            [serial]: {
              loading: false,
              present: false,
              status: 'unknown',
              message: String(e?.message ?? e),
            },
          }));
        }
      });
    })();

    return () => controller.abort();
  }, [servers, companyId, assetService]);

  //get topology, servers, sorted list view,....
  const derived = useServerTopologyModel({
    servers,
    rmmBySerial,
    query,
    showOnlyRunning,
  });

  const onEntityClick = (entityId: string) => {
    const node = derived.nodeByEntityId[entityId];
    if (node) openNodeAside(node as Node<TopologyNodeData>);
  };

  return (
    <div className="w-full mt-5 space-y-3">
      <ServerParkSummary
        stats={derived.stats}
        issues={derived.issues}
        query={query}
        onQueryChange={setQuery}
        showOnlyRunning={showOnlyRunning}
        onShowOnlyRunningChange={setShowOnlyRunning}
        onReset={() => {
          setQuery('');
          setShowOnlyRunning(false);
        }}
        onIssueClick={(entityId) => onEntityClick(entityId)}
      />
      <div className="relative w-48 h-10 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-1 grid grid-cols-2">
        {/* ✅ Sliding background */}
        <div
          className={`absolute top-1 bottom-1 w-[calc(50%-6px)] rounded-lg bg-white dark:bg-slate-800 shadow-sm transition-all duration-300`}
          style={{
            left: view === 'topology' ? '4px' : 'calc(50% + 2px)',
          }}
        />

        <button
          onClick={() => setView('topology')}
          className="z-10 text-sm font-semibold"
        >
          Topology
        </button>

        <button
          onClick={() => setView('list')}
          className="z-10 text-sm font-semibold"
        >
          Lijst
        </button>
      </div>

      <div className="flex w-full h-[80vh] rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="flex-1">
          {view === 'topology' ? (
            <ReactFlow
              nodes={derived.nodes}
              edges={derived.edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              fitView
              zoomOnScroll
              style={{ height: '100%' }}
              onNodeClick={(_, node) =>
                openNodeAside(node as Node<TopologyNodeData>)
              }
            >
              <Background gap={16} />
              <Controls position="top-left" />
              <MiniMap
                pannable
                zoomable
                nodeStrokeWidth={3}
                nodeColor={miniMapColor}
                position="top-right"
              />
            </ReactFlow>
          ) : (
            <ServerParkListView
              rows={derived.rows}
              onRowClick={(entityId) => onEntityClick(entityId)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default AssetServersTopology;
